import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api, type Anagrafica, type Categoria, type Documento } from '../api';
import {
  Campo,
  CampoImporto,
  Caricamento,
  Dialogo,
  Errore,
  Etichetta,
  Importo,
  Scheda,
  Vuoto,
  useAvvisi,
  useCarico,
  useDebounce,
  useFiltriUrl,
} from '../componenti/base';
import { etichettaStato, formattaData, formattaEuro, oggi } from '../formato';
import { useSessione } from '../main';

const STATI = [
  { v: '', t: 'Tutti gli stati' },
  { v: 'APERTI', t: 'Non saldati' },
  { v: 'SCADUTA', t: 'Scadute' },
  { v: 'DA_PAGARE', t: 'Da saldare' },
  { v: 'PARZIALE', t: 'Parziali' },
  { v: 'PAGATA', t: 'Saldate' },
];

export function Documenti({ tipo }: { tipo: 'ATTIVA' | 'PASSIVA' }) {
  const { organizzazione } = useSessione();
  // Nell'indirizzo, non nello stato: entrare in una fattura e tornare indietro
  // non deve far ricominciare la ricerca da capo. Con più fatture della stessa
  // controparte da lavorare, quel giro si rifà una volta per fattura.
  const { filtri, imposta, azzera, qualcuno } = useFiltriUrl({ stato: '', cerca: '', da: '', a: '' });
  const percorso = useLocation();
  const { stato, cerca, da, a } = filtri;
  const [nuovo, setNuovo] = useState(false);
  const cercaRitardata = useDebounce(cerca);

  const query = new URLSearchParams({ tipo });
  if (stato) query.set('stato', stato);
  if (cercaRitardata) query.set('cerca', cercaRitardata);
  if (da) query.set('da', da);
  if (a) query.set('a', a);

  const { dati, caricamento, errore, ricarica } = useCarico(
    () => api.get<Documento[]>(`/api/documenti?${query}`),
    [tipo, stato, cercaRitardata, da, a],
  );

  const totale = (dati ?? []).reduce((t, d) => t + d.totaleCent, 0);
  const residuo = (dati ?? []).reduce((t, d) => t + d.residuoCent, 0);
  const adesso = oggi();

  return (
    <div className="pagina space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">
            {tipo === 'ATTIVA' ? 'Fatture attive' : 'Fatture passive'}
          </h1>
          <p className="text-sm text-inchiostro-500">
            {tipo === 'ATTIVA' ? 'Emesse verso i clienti' : 'Ricevute dai fornitori'} ·{' '}
            {dati?.length ?? 0} document{(dati?.length ?? 0) === 1 ? 'o' : 'i'}, residuo{' '}
            <span className="num font-medium">{formattaEuro(residuo)}</span> su {formattaEuro(totale)}
          </p>
        </div>
        <button className="bottone-primario" onClick={() => setNuovo(true)}>
          Nuova fattura {tipo === 'ATTIVA' ? 'attiva' : 'passiva'}
        </button>
      </header>

      <Scheda senzaPadding>
        <div className="flex flex-wrap items-end gap-3 border-b border-inchiostro-200 p-3">
          <Campo etichetta="Cerca" larghezza="w-60">
            <input
              className="campo"
              placeholder="Numero o controparte"
              value={cerca}
              onChange={(e) => imposta({ cerca: e.target.value })}
            />
          </Campo>
          <Campo etichetta="Stato" larghezza="w-44">
            <select className="campo" value={stato} onChange={(e) => imposta({ stato: e.target.value })}>
              {STATI.map((s) => (
                <option key={s.v} value={s.v}>
                  {s.t}
                </option>
              ))}
            </select>
          </Campo>
          <Campo etichetta="Competenza dal" larghezza="w-40">
            <input type="date" className="campo" value={da} onChange={(e) => imposta({ da: e.target.value })} />
          </Campo>
          <Campo etichetta="al" larghezza="w-40">
            <input type="date" className="campo" value={a} onChange={(e) => imposta({ a: e.target.value })} />
          </Campo>
          {qualcuno && (
            <button className="bottone-secondario" onClick={azzera}>
              Azzera filtri
            </button>
          )}
        </div>

        {caricamento ? (
          <Caricamento />
        ) : errore ? (
          <div className="p-4">
            <Errore testo={errore} riprova={ricarica} />
          </div>
        ) : (dati ?? []).length === 0 ? (
          <Vuoto
            titolo="Nessuna fattura con questi filtri"
            testo="Puoi crearne una a mano, importare un XML FatturaPA o sincronizzare con Fatture in Cloud."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="dati">
              <thead>
                <tr>
                  <th>Numero</th>
                  <th>Controparte</th>
                  <th>Competenza</th>
                  <th title="Scadenza corretta col ritardo medio storico della controparte">Cassa attesa</th>
                  <th className="text-right">Imponibile</th>
                  <th className="text-right">IVA</th>
                  <th className="text-right">Totale</th>
                  <th className="text-right">Residuo</th>
                  <th>Stato</th>
                  <th>Origine</th>
                </tr>
              </thead>
              <tbody>
                {dati!.map((d) => (
                  <tr key={d.id}>
                    <td className="whitespace-nowrap">
                      {/* Da dove si è arrivati, così il «torna indietro» del
                          dettaglio riporta all'elenco filtrato e non a quello
                          intero. Il tasto del browser lo fa già da sé perché i
                          filtri stanno nell'indirizzo: questo serve al link. */}
                      <Link
                        to={`/fatture/${d.id}`}
                        state={{ elenco: `${percorso.pathname}${percorso.search}` }}
                        className="font-medium underline underline-offset-2"
                      >
                        {d.numero}
                      </Link>
                      {d.notaDiCredito && (
                        <span className="ml-1.5">
                          <Etichetta testo="NC" tono="attenzione" titolo="Nota di credito: il flusso va nel verso opposto" />
                        </span>
                      )}
                    </td>
                    <td className="max-w-56 truncate">{d.controparte?.denominazione ?? '—'}</td>
                    <td className="num whitespace-nowrap">{formattaData(d.dataCompetenza, organizzazione.formatoData)}</td>
                    <td className="num whitespace-nowrap">
                      {formattaData(d.dataCassaAttesa, organizzazione.formatoData)}
                    </td>
                    <td className="text-right"><Importo cent={d.imponibileCent} /></td>
                    <td className="text-right"><Importo cent={d.ivaCent} /></td>
                    <td className="text-right"><Importo cent={d.totaleCent} grassetto /></td>
                    <td className="text-right">
                      <Importo cent={d.residuoCent} tono={d.residuoCent === 0 ? 'neutro' : tipo === 'ATTIVA' ? 'incassi' : 'uscite'} />
                    </td>
                    <td>
                      <StatoDocumento documento={d} adesso={adesso} />
                    </td>
                    <td className="text-xs text-inchiostro-500">
                      {d.origine === 'XML_SDI' ? 'XML SDI' : d.origine === 'FATTURA_IN_CLOUD' ? 'Fatture in Cloud' : 'Manuale'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Scheda>

      {nuovo && (
        <FormNuovaFattura
          tipo={tipo}
          onChiudi={() => setNuovo(false)}
          onCreata={() => {
            setNuovo(false);
            ricarica();
          }}
        />
      )}
    </div>
  );
}

export function StatoDocumento({ documento, adesso }: { documento: Documento; adesso: string }) {
  const s = documento.statoEsteso;
  const tono = s === 'PAGATA' ? 'ok' : s === 'SCADUTA' ? 'allerta' : s === 'PARZIALE' ? 'attenzione' : 'neutro';
  return (
    <div className="flex flex-col items-start gap-1">
      <Etichetta testo={etichettaStato(s, documento.tipo)} tono={tono} />
      {/* Lo stato riferito dal gestionale è un dato diverso dalla
          riconciliazione bancaria: si mostra accanto, non al posto. */}
      {documento.statoOrigineEsterna === 'PAGATA' && documento.stato !== 'PAGATA' && (
        <Etichetta
          testo="pagata su FIC"
          tono="info"
          titolo={`Fatture in Cloud la dà per pagata${
            documento.dataPagamentoEsterna ? ` il ${documento.dataPagamentoEsterna}` : ''
          }, ma non risulta ancora un movimento bancario abbinato.`}
        />
      )}
    </div>
  );
}

// ── Creazione manuale ───────────────────────────────────────────────────────

interface RigaForm {
  descrizione: string;
  quantita: string;
  unitaMisura: string;
  prezzoUnitarioCent: number;
  aliquotaIva: string;
}

const rigaVuota = (aliquota: string): RigaForm => ({
  descrizione: '',
  quantita: '1',
  unitaMisura: '',
  prezzoUnitarioCent: 0,
  aliquotaIva: aliquota,
});

function FormNuovaFattura({
  tipo,
  onChiudi,
  onCreata,
}: {
  tipo: 'ATTIVA' | 'PASSIVA';
  onChiudi: () => void;
  onCreata: () => void;
}) {
  const { organizzazione } = useSessione();
  const avvisa = useAvvisi();
  const { dati: anagrafiche } = useCarico(
    () => api.get<Anagrafica[]>(`/api/anagrafiche?tipo=${tipo === 'ATTIVA' ? 'CLIENTE' : 'FORNITORE'}`),
    [tipo],
  );
  const { dati: categorie } = useCarico(() => api.get<Categoria[]>('/api/categorie'), []);

  const [numero, setNumero] = useState('');
  const [anagraficaId, setAnagraficaId] = useState('');
  const [dataCompetenza, setDataCompetenza] = useState(oggi());
  const [dataScadenza, setDataScadenza] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [causale, setCausale] = useState('');
  const [righe, setRighe] = useState<RigaForm[]>([rigaVuota(organizzazione.aliquotaIvaDefault)]);
  const [salvataggio, setSalvataggio] = useState(false);

  const calcolate = righe.map((r) => ({
    ...r,
    prezzoTotaleCent: Math.round(Number(r.quantita.replace(',', '.') || 0) * r.prezzoUnitarioCent),
  }));
  const imponibile = calcolate.reduce((t, r) => t + r.prezzoTotaleCent, 0);
  const perAliquota = new Map<string, number>();
  for (const r of calcolate) {
    const k = Number(r.aliquotaIva).toFixed(2);
    perAliquota.set(k, (perAliquota.get(k) ?? 0) + r.prezzoTotaleCent);
  }
  const iva = [...perAliquota].reduce((t, [al, imp]) => t + Math.round((imp * Number(al)) / 100), 0);
  const totale = imponibile + iva;

  const salva = async (e: React.FormEvent) => {
    e.preventDefault();
    setSalvataggio(true);
    try {
      await api.post('/api/documenti', {
        tipo,
        numero,
        anagraficaId,
        dataCompetenza,
        dataScadenzaNominale: dataScadenza || dataCompetenza,
        categoriaId: categoriaId || null,
        causale: causale || null,
        righe: calcolate.map((r, i) => ({
          numeroLinea: i + 1,
          descrizione: r.descrizione,
          quantita: r.quantita.replace(',', '.'),
          unitaMisura: r.unitaMisura || null,
          prezzoUnitarioCent: r.prezzoUnitarioCent,
          prezzoTotaleCent: r.prezzoTotaleCent,
          aliquotaIva: Number(r.aliquotaIva).toFixed(2),
        })),
      });
      avvisa(`Fattura ${numero} creata.`);
      onCreata();
    } catch (err) {
      avvisa(err instanceof Error ? err.message : 'Salvataggio non riuscito.', 'errore');
    } finally {
      setSalvataggio(false);
    }
  };

  const categorieVerso = (categorie ?? []).filter(
    (c) => c.verso === (tipo === 'ATTIVA' ? 'ENTRATA' : 'USCITA') && !c.sistema,
  );

  return (
    <Dialogo aperto titolo={`Nuova fattura ${tipo === 'ATTIVA' ? 'attiva' : 'passiva'}`} onChiudi={onChiudi} larghezza="max-w-4xl">
      <form onSubmit={salva} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Campo etichetta="Numero">
            <input className="campo" required value={numero} onChange={(e) => setNumero(e.target.value)} />
          </Campo>
          <Campo etichetta={tipo === 'ATTIVA' ? 'Cliente' : 'Fornitore'}>
            <select className="campo" required value={anagraficaId} onChange={(e) => setAnagraficaId(e.target.value)}>
              <option value="">Scegli…</option>
              {(anagrafiche ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.denominazione}
                </option>
              ))}
            </select>
          </Campo>
          <Campo etichetta="Voce del previsionale" aiuto="Se non la scegli, vale quella dell’anagrafica.">
            <select className="campo" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
              <option value="">Dall’anagrafica</option>
              {categorieVerso.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo etichetta="Data di competenza" aiuto="Alimenta il conto economico.">
            <input
              type="date"
              className="campo"
              required
              value={dataCompetenza}
              onChange={(e) => setDataCompetenza(e.target.value)}
            />
          </Campo>
          <Campo
            etichetta="Scadenza"
            aiuto="La data di cassa attesa la calcola Mastro sommando il ritardo medio della controparte."
          >
            <input type="date" className="campo" value={dataScadenza} onChange={(e) => setDataScadenza(e.target.value)} />
          </Campo>
          <Campo etichetta="Causale">
            <input className="campo" value={causale} onChange={(e) => setCausale(e.target.value)} />
          </Campo>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-medium">Righe</h3>
            <button
              type="button"
              className="bottone-secondario"
              onClick={() => setRighe([...righe, rigaVuota(organizzazione.aliquotaIvaDefault)])}
            >
              Aggiungi riga
            </button>
          </div>
          <div className="overflow-x-auto rounded-md border border-inchiostro-200">
            <table className="dati">
              <thead>
                <tr>
                  <th className="min-w-64">Descrizione</th>
                  <th className="w-24">Qtà</th>
                  <th className="w-20">U.M.</th>
                  <th className="w-32 text-right">Prezzo unit.</th>
                  <th className="w-24">IVA %</th>
                  <th className="w-32 text-right">Totale riga</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {righe.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <input
                        className="campo"
                        required
                        value={r.descrizione}
                        onChange={(e) => setRighe(righe.map((x, j) => (i === j ? { ...x, descrizione: e.target.value } : x)))}
                      />
                    </td>
                    <td>
                      <input
                        className="campo num text-right"
                        inputMode="decimal"
                        value={r.quantita}
                        onChange={(e) => setRighe(righe.map((x, j) => (i === j ? { ...x, quantita: e.target.value } : x)))}
                      />
                    </td>
                    <td>
                      <input
                        className="campo"
                        value={r.unitaMisura}
                        onChange={(e) => setRighe(righe.map((x, j) => (i === j ? { ...x, unitaMisura: e.target.value } : x)))}
                      />
                    </td>
                    <td>
                      <CampoImporto
                        valoreCent={r.prezzoUnitarioCent}
                        onCambia={(c) => setRighe(righe.map((x, j) => (i === j ? { ...x, prezzoUnitarioCent: c } : x)))}
                      />
                    </td>
                    <td>
                      <input
                        className="campo num text-right"
                        inputMode="decimal"
                        value={r.aliquotaIva}
                        onChange={(e) => setRighe(righe.map((x, j) => (i === j ? { ...x, aliquotaIva: e.target.value } : x)))}
                      />
                    </td>
                    <td className="text-right align-middle">
                      <Importo cent={calcolate[i].prezzoTotaleCent} />
                    </td>
                    <td className="align-middle">
                      {righe.length > 1 && (
                        <button
                          type="button"
                          className="text-inchiostro-400 hover:text-allerta"
                          onClick={() => setRighe(righe.filter((_, j) => j !== i))}
                          aria-label="Elimina riga"
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end">
          <dl className="w-64 space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-inchiostro-600">Imponibile</dt>
              <dd><Importo cent={imponibile} /></dd>
            </div>
            {[...perAliquota].map(([al, imp]) => (
              <div key={al} className="flex justify-between text-xs text-inchiostro-500">
                <dt>IVA {Number(al).toLocaleString('it-IT')}% su {formattaEuro(imp)}</dt>
                <dd className="num">{formattaEuro(Math.round((imp * Number(al)) / 100))}</dd>
              </div>
            ))}
            <div className="flex justify-between border-t border-inchiostro-200 pt-1 font-semibold">
              <dt>Totale</dt>
              <dd><Importo cent={totale} grassetto /></dd>
            </div>
          </dl>
        </div>

        <div className="flex justify-end gap-2 border-t border-inchiostro-200 pt-4">
          <button type="button" className="bottone-secondario" onClick={onChiudi}>
            Annulla
          </button>
          <button type="submit" className="bottone-primario" disabled={salvataggio}>
            {salvataggio ? 'Salvataggio…' : 'Crea fattura'}
          </button>
        </div>
      </form>
    </Dialogo>
  );
}
